import React, { useCallback, useEffect, useLayoutEffect, useRef } from "react";
import { useDesktopDragScroll } from "../../hooks/useDesktopDragScroll";
import ProfileMapCard from "./ProfileMapCard";
import "./ProfileCarousel.css";

const MAP_PAN_MS = 430;
const PROGRAMMATIC_SCROLL_MS = 520;

export default function ProfileCarousel({
  profiles,
  selectedProfileId,
  onProfilePress,
  onSelectedChange,
}) {
  const isUserDraggingRef = useRef(false);
  const isProgrammaticScrollRef = useRef(false);
  const hasUserGestureRef = useRef(false);
  const lastSyncedIndexRef = useRef(-1);
  const lastDragIndexRef = useRef(-1);
  const scrollRafRef = useRef(null);
  const openTimerRef = useRef(null);
  const programmaticTimerRef = useRef(null);
  const wheelSettleTimerRef = useRef(null);
  const scrollerRef = useRef(null);

  const syncCarouselLayout = useCallback(() => {
    const track = scrollerRef.current;
    if (!track) return;

    const isDesktop = window.matchMedia("(min-width: 900px)").matches;
    if (!isDesktop) {
      track.dataset.carouselLayout = "scroll";
      return;
    }

    const slots = track.querySelectorAll(".profile-carousel__slot");
    if (!slots.length) {
      track.dataset.carouselLayout = "scroll";
      return;
    }

    const gap = Number.parseFloat(getComputedStyle(track).columnGap || getComputedStyle(track).gap) || 10;
    let totalWidth = 0;

    slots.forEach((slot, index) => {
      totalWidth += slot.getBoundingClientRect().width;
      if (index > 0) totalWidth += gap;
    });

    track.dataset.carouselLayout = totalWidth <= track.clientWidth + 1 ? "group" : "scroll";
  }, []);

  const clearProgrammaticScroll = useCallback(() => {
    if (programmaticTimerRef.current) {
      window.clearTimeout(programmaticTimerRef.current);
      programmaticTimerRef.current = null;
    }
    isProgrammaticScrollRef.current = false;
  }, []);

  const beginProgrammaticScroll = useCallback((smooth = true) => {
    clearProgrammaticScroll();
    isProgrammaticScrollRef.current = true;
    hasUserGestureRef.current = false;

    if (smooth) {
      programmaticTimerRef.current = window.setTimeout(() => {
        isProgrammaticScrollRef.current = false;
        programmaticTimerRef.current = null;
      }, PROGRAMMATIC_SCROLL_MS);
    }
  }, [clearProgrammaticScroll]);

  const reportSelectionAtIndex = useCallback(
    (index) => {
      const profile = profiles[index];
      if (!profile?.id) return;

      lastSyncedIndexRef.current = index;
      lastDragIndexRef.current = index;
      if (profile.id !== selectedProfileId) {
        onSelectedChange?.(profile.id);
      }
    },
    [onSelectedChange, profiles, selectedProfileId],
  );

  const resolveCenteredIndex = useCallback(() => {
    const container = scrollerRef.current;
    if (!container) return -1;

    const containerRect = container.getBoundingClientRect();
    const centerX = containerRect.left + containerRect.width / 2;
    let closestIndex = -1;
    let closestDist = Infinity;

    Array.from(container.children).forEach((child, index) => {
      const rect = child.getBoundingClientRect();
      const childCenter = rect.left + rect.width / 2;
      const dist = Math.abs(childCenter - centerX);
      if (dist < closestDist) {
        closestDist = dist;
        closestIndex = index;
      }
    });

    return closestIndex;
  }, []);

  const scrollToProfile = useCallback((profileId, smooth = true) => {
    const index = profiles.findIndex((p) => p.id === profileId);
    const container = scrollerRef.current;
    if (index < 0 || !container) return;

    lastSyncedIndexRef.current = index;
    lastDragIndexRef.current = index;
    beginProgrammaticScroll(smooth);

    const card = container.children[index];
    if (card) {
      card.scrollIntoView({
        behavior: smooth ? "smooth" : "auto",
        inline: "center",
        block: "nearest",
      });
    }
  }, [beginProgrammaticScroll, profiles]);

  const syncSelectionFromScroll = useCallback(() => {
    if (!scrollerRef.current || isProgrammaticScrollRef.current) return;

    const closestIndex = resolveCenteredIndex();
    if (closestIndex >= 0) {
      reportSelectionAtIndex(closestIndex);
    }
  }, [reportSelectionAtIndex, resolveCenteredIndex]);

  const handleLiveScroll = useCallback(() => {
    if (!scrollerRef.current || isProgrammaticScrollRef.current) return;

    const closestIndex = resolveCenteredIndex();
    if (closestIndex < 0 || closestIndex === lastDragIndexRef.current) return;

    reportSelectionAtIndex(closestIndex);
  }, [reportSelectionAtIndex, resolveCenteredIndex]);

  const scheduleScrollSync = useCallback(() => {
    if (scrollRafRef.current) {
      window.cancelAnimationFrame(scrollRafRef.current);
    }
    scrollRafRef.current = window.requestAnimationFrame(handleLiveScroll);
  }, [handleLiveScroll]);

  const handleScrollSettled = useCallback(() => {
    if (isProgrammaticScrollRef.current) {
      isProgrammaticScrollRef.current = false;
      clearProgrammaticScroll();
      return;
    }

    const shouldSync = hasUserGestureRef.current;
    isUserDraggingRef.current = false;
    clearProgrammaticScroll();

    if (!shouldSync) return;

    hasUserGestureRef.current = false;
    syncSelectionFromScroll();
  }, [clearProgrammaticScroll, syncSelectionFromScroll]);

  const handleScrollBegin = useCallback(() => {
    isUserDraggingRef.current = true;
    hasUserGestureRef.current = true;
    clearProgrammaticScroll();
    lastDragIndexRef.current = lastSyncedIndexRef.current;
  }, [clearProgrammaticScroll]);

  const scheduleWheelSettled = useCallback(() => {
    if (wheelSettleTimerRef.current) {
      window.clearTimeout(wheelSettleTimerRef.current);
    }
    wheelSettleTimerRef.current = window.setTimeout(() => {
      wheelSettleTimerRef.current = null;
      handleScrollSettled();
    }, 140);
  }, [handleScrollSettled]);

  const handleWheelScroll = useCallback((event, el) => {
    if (isProgrammaticScrollRef.current) return;

    const delta = Math.abs(event.deltaX) > Math.abs(event.deltaY)
      ? event.deltaX
      : (event.shiftKey ? event.deltaY : 0);
    if (!delta) return;

    event.preventDefault();
    handleScrollBegin();
    el.scrollLeft += delta;
    scheduleScrollSync();
    scheduleWheelSettled();
  }, [handleScrollBegin, scheduleScrollSync, scheduleWheelSettled]);

  const { suppressClickRef } = useDesktopDragScroll({
    ref: scrollerRef,
    axis: "x",
    onDragStart: handleScrollBegin,
    onDragMove: scheduleScrollSync,
    onDragEnd: handleScrollSettled,
    onWheelScroll: handleWheelScroll,
  });

  const handleCardClick = useCallback(
    (profile) => {
      if (suppressClickRef.current) {
        suppressClickRef.current = false;
        return;
      }

      if (openTimerRef.current) {
        window.clearTimeout(openTimerRef.current);
        openTimerRef.current = null;
      }

      const index = profiles.findIndex((p) => p.id === profile.id);
      if (index < 0) return;

      const isAlreadySelected = profile.id === selectedProfileId;
      hasUserGestureRef.current = false;
      isUserDraggingRef.current = false;
      lastSyncedIndexRef.current = index;
      lastDragIndexRef.current = index;
      onSelectedChange?.(profile.id);

      if (!isAlreadySelected) {
        scrollToProfile(profile.id, true);
      }

      const openDelay = isAlreadySelected ? 0 : MAP_PAN_MS;
      openTimerRef.current = window.setTimeout(() => {
        onProfilePress?.(profile);
        openTimerRef.current = null;
      }, openDelay);
    },
    [onProfilePress, onSelectedChange, profiles, scrollToProfile, selectedProfileId, suppressClickRef],
  );

  useLayoutEffect(() => {
    syncCarouselLayout();

    const track = scrollerRef.current;
    if (!track) return undefined;

    const observer = new ResizeObserver(() => {
      syncCarouselLayout();
    });

    observer.observe(track);
    window.addEventListener("resize", syncCarouselLayout);

    return () => {
      observer.disconnect();
      window.removeEventListener("resize", syncCarouselLayout);
    };
  }, [profiles, syncCarouselLayout]);

  useEffect(() => {
    if (!selectedProfileId || isUserDraggingRef.current) return;

    const index = profiles.findIndex((p) => p.id === selectedProfileId);
    if (index < 0 || index === lastSyncedIndexRef.current) return;

    scrollToProfile(selectedProfileId, true);
  }, [profiles, scrollToProfile, selectedProfileId]);

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return undefined;

    const onScroll = () => {
      if (isProgrammaticScrollRef.current) return;
      scheduleScrollSync();
    };

    const onScrollEnd = () => handleScrollSettled();

    el.addEventListener("scroll", onScroll, { passive: true });
    if ("onscrollend" in el) {
      el.addEventListener("scrollend", onScrollEnd);
    }

    return () => {
      el.removeEventListener("scroll", onScroll);
      if ("onscrollend" in el) {
        el.removeEventListener("scrollend", onScrollEnd);
      }
      if (scrollRafRef.current) {
        window.cancelAnimationFrame(scrollRafRef.current);
      }
      if (openTimerRef.current) {
        window.clearTimeout(openTimerRef.current);
      }
      if (wheelSettleTimerRef.current) {
        window.clearTimeout(wheelSettleTimerRef.current);
      }
      clearProgrammaticScroll();
    };
  }, [clearProgrammaticScroll, handleScrollSettled, scheduleScrollSync, scrollerRef]);

  if (!profiles.length) return null;

  return (
    <div className="profile-carousel" aria-label="Nearby profiles">
      <div className="profile-carousel__track" ref={scrollerRef} data-carousel-layout="scroll">
        {profiles.map((profile) => (
          <div key={profile.id} className="profile-carousel__slot">
            <ProfileMapCard
              profile={profile}
              selected={profile.id === selectedProfileId}
              onClick={handleCardClick}
            />
          </div>
        ))}
      </div>
    </div>
  );
}
